import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Alert, AlertDescription } from '../ui/Alert';
import { dashboardService } from '../../services/dashboard.service';
import { handleApiCall, displayError } from '../../lib/error-handler';
import { formatDate } from '../../lib/utils';
import { Trash2, Plus, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import type { RSSFeed } from '../../types/api';

interface FeedManagerProps {
  feeds: RSSFeed[];
  onChange: (feeds: RSSFeed[]) => void;
  disabled?: boolean;
}

export function FeedManager({ feeds, onChange, disabled }: FeedManagerProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFeed, setNewFeed] = useState({ url: '', name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddFeed = async () => {
    if (!newFeed.url || !newFeed.name) {
      setError('URL and name are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Test the feed first
      setTesting(true);
      const testResult = await handleApiCall(
        () => dashboardService.testFeed(newFeed.url),
        'Failed to validate feed'
      );
      setTesting(false);

      if (!testResult.valid) {
        setError('Invalid RSS feed URL. Please check the URL and try again.');
        setLoading(false);
        return;
      }

      // Create the feed
      const feed = await handleApiCall(
        () => dashboardService.createFeed({
          url: newFeed.url,
          name: newFeed.name,
          description: newFeed.description || undefined,
          is_active: true
        }),
        'Failed to add feed'
      );

      onChange([...feeds, feed]);
      setNewFeed({ url: '', name: '', description: '' });
      setShowAddForm(false);
    } catch (err) {
      setError(displayError(err));
    } finally {
      setLoading(false);
      setTesting(false);
    }
  };

  const handleToggleFeed = async (feed: RSSFeed) => {
    try {
      const updated = await handleApiCall(
        () => dashboardService.updateFeed(feed.id, { is_active: !feed.is_active }),
        'Failed to update feed'
      );

      onChange(feeds.map(f => f.id === feed.id ? updated : f));
    } catch (err) {
      setError(displayError(err));
    }
  };

  const handleDeleteFeed = async (feedId: string) => {
    if (!window.confirm('Are you sure you want to delete this feed?')) {
      return;
    }

    try {
      await handleApiCall(
        () => dashboardService.deleteFeed(feedId),
        'Failed to delete feed'
      );

      onChange(feeds.filter(f => f.id !== feedId));
    } catch (err) {
      setError(displayError(err));
    }
  };

  const activeFeeds = feeds.filter(f => f.is_active);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>RSS Feeds</CardTitle>
            <CardDescription>
              Manage your RSS feed sources ({activeFeeds.length} active, {feeds.length} total)
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={disabled}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Feed
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {showAddForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
            <h4 className="font-medium">Add New Feed</h4>

            <div className="space-y-2">
              <Label htmlFor="feed-url">Feed URL *</Label>
              <Input
                id="feed-url"
                type="url"
                placeholder="https://example.com/feed.xml"
                value={newFeed.url}
                onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feed-name">Name *</Label>
              <Input
                id="feed-name"
                placeholder="My RSS Feed"
                value={newFeed.name}
                onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feed-description">Description (optional)</Label>
              <Input
                id="feed-description"
                placeholder="Brief description"
                value={newFeed.description}
                onChange={(e) => setNewFeed({ ...newFeed, description: e.target.value })}
                disabled={loading}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleAddFeed} disabled={loading} size="sm">
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : loading ? (
                  'Adding...'
                ) : (
                  'Add Feed'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewFeed({ url: '', name: '', description: '' });
                  setError(null);
                }}
                disabled={loading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {feeds.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No RSS feeds added yet.</p>
              <p className="text-sm mt-1">Click "Add Feed" to get started.</p>
            </div>
          ) : (
            feeds.map((feed) => (
              <div
                key={feed.id}
                className="border rounded-lg p-3 flex items-start justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium truncate">{feed.name}</h4>
                    {feed.is_active ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate mb-1">
                    {feed.url}
                  </p>
                  {feed.last_fetched_at && (
                    <p className="text-xs text-muted-foreground">
                      Last fetched: {formatDate(feed.last_fetched_at)}
                      {feed.last_article_count && ` (${feed.last_article_count} articles)`}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 ml-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleFeed(feed)}
                    disabled={disabled}
                  >
                    {feed.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteFeed(feed.id)}
                    disabled={disabled}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
